import { Box, Stack, Drawer, useTheme, IconButton } from '@mui/material';
import { useLocation } from 'react-router';

import { NAV } from '@app/configs';
import { Logo, Iconify } from '@app/components';
import Scrollbar from '@app/components/scrollbar/Scrollbar';
import NavSectionVertical from '@app/components/nav-section/vertical/NavSectionVertical';
import useResponsive from '@app/hooks/use-responsive';
import { CloseIconStyled } from '../style';
import navConfig from './config-navigation';
import NavAccount from './NavAccount';
import { useAuthRole, useAuthRolePermissions } from '@app/utils';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { NavListProps } from '@app/components/nav-section/types';

// ----------------------------------------------------------------------

interface Props {
  openNav: boolean;
  onCloseNav: VoidFunction;
}

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const theme = useTheme();

  const isMobile = useResponsive('down', 'sm');
  const { isSuperAdmin, isEmployee, isIntern, isManager } = useAuthRole();
  const { hasPermission } = useAuthRolePermissions();
  const location = useLocation();
  const isTaskBoardPath = location.pathname.includes('taskBoard');

  const permissions = {
    Assets: hasPermission('Assets', 'r'),
    Candidates: hasPermission('Candidates', 'r'),
    Client: hasPermission('Client', 'r'),
    Compensation: hasPermission('Compensation', 'r'),
    Departments: hasPermission('Departments', 'r'),
    Designations: hasPermission('Designations', 'r'),
    Employees: hasPermission('Employees', 'r'),
    Holiday: hasPermission('Holiday', 'r'),
    LeaveManagement: hasPermission('LeaveManagement', 'r'),
    Leaves: hasPermission('Leaves', 'r'),
    OfferLetter: hasPermission('OfferLetter', 'r'),
    Projects: hasPermission('Projects', 'r'),
    Roles: hasPermission('Roles', 'r'),
    Interviewee: hasPermission('Interviewee', 'r'),
    Intern: hasPermission('Intern', 'r'),
    Reviews: hasPermission('Reviews', 'r'),
    Boards: hasPermission('Task Board', 'r'),
    Lists: hasPermission('Lists', 'r'),
    Cards: hasPermission('Cards', 'r'),
    Checklist: hasPermission('Checklist', 'r'),
    Labels: hasPermission('Labels', 'r'),
    Dates: hasPermission('Dates', 'r'),
    Comments: hasPermission('Comments', 'r'),
    Attachments: hasPermission('Attachments', 'r'),
    Transactions: hasPermission('Transactions', 'r'),
    Tasks: hasPermission('Tasks', 'r'),
    CardMembers: hasPermission('Card Members', 'r'),
    BoardMembers: hasPermission('Board Members', 'r'),
    BoardImages: hasPermission('Board Images', 'r'),
    TaskBoard: hasPermission('Task Board', 'r'),
  };

  const filteredNavConfig = navConfig
    .filter((item) => !item.hidden)
    .map((item) => {
      if (isSuperAdmin) {
        return item;
      }
      if (item.path === PATH_DASHBOARD.app) return item;
      if (item.children) {
        const children = item.children
          .map((child) => {
            if (isEmployee && child.title === 'Employees') return false;

            if ((isEmployee || isIntern) && child.title === 'Overdue Tasks') return null;

            if (child.path === PATH_DASHBOARD.eta && !isSuperAdmin && !isManager) {
              return null;
            }

            if (isManager && child.title === 'Employees') {
              return {
                ...child,
                title: 'My Team Members',
              };
            }

            const SplitChildPath = child.path.split('/');
            const childLastPart = SplitChildPath[SplitChildPath.length - 1].toLowerCase();

            const hasPermissionToView = Object.entries(permissions).some(
              ([module, permission]) => permission && childLastPart.includes(module.toLowerCase())
            );

            return hasPermissionToView ? child : null;
          })
          .filter(Boolean);
        if (children.length > 0) {
          return { ...item, children };
        }
        return null;
      }
      const ItemWithNoChild = Object.entries(permissions).some(
        ([module, permission]) =>
          permission && item.path.toLowerCase().includes(module.toLowerCase())
      );

      return ItemWithNoChild ? item : null;
    })
    .filter(Boolean) as NavListProps[];

  const renderContent = (
    <Stack
      sx={{
        background: isMobile
          ? `url(/assets/background/auth-bg.png) center center/cover`
          : openNav
          ? 'grey'
          : `url(/assets/background/auth-bg.png) center center/cover`,
      }}
    >
      {openNav && !isMobile && (
        <Stack>
          <Iconify
            icon="weui:arrow-outlined"
            onClick={onCloseNav}
            fontWeight={900}
            sx={{
              position: 'absolute',
              top: '7%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 40,
              height: 40,
              color: theme.palette.error.contrastText,
            }}
          />
        </Stack>
      )}
      <Scrollbar
        sx={{
          height: '100vh',
          '& .simplebar-content': {
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <CloseIconStyled>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={onCloseNav}>
            <Iconify icon="fluent-emoji-high-contrast:cross-mark" />
          </IconButton>
        </CloseIconStyled>
        <Stack
          sx={{
            px: 2.5,
            flexShrink: 0,
          }}
          direction={'row'}
          gap={6}
        >
          <Logo disabledLink sx={{ height: isMobile ? 90 : 127, width: 156 }} />
          {isTaskBoardPath && !openNav && (
            <Stack onClick={onCloseNav}>
              <Iconify
                icon={'fluent:ios-arrow-24-filled'}
                width={25}
                fontWeight={900}
                color={theme.palette.error.contrastText}
                sx={{
                  position: 'absolute',
                  right: -2,
                  top: 50,
                }}
              />
            </Stack>
          )}
        </Stack>

        <Stack sx={{ mx: 2, my: 1.75, flexGrow: 1 }}>
          <NavSectionVertical sx={{ flexGrow: 1 }} data={filteredNavConfig} />
        </Stack>

        <Stack sx={{ mx: 2.5, pb: 3 }}>
          <NavAccount />
        </Stack>
      </Scrollbar>
    </Stack>
  );
  return (
    <Box
      component="nav"
      sx={{
        width: { lg: openNav ? 20 : NAV.W_DASHBOARD },
      }}
    >
      <Drawer
        anchor="left"
        open={openNav}
        onClose={onCloseNav}
        variant={isMobile ? 'temporary' : 'permanent'}
        PaperProps={{
          sx: {
            zIndex: 0,
            width: isMobile ? NAV.W_DASHBOARD : openNav ? 20 : NAV.W_DASHBOARD,
            bgcolor: theme.palette.primary.darker,
            position: 'relative',
            display: openNav || !isMobile ? 'visible' : 'none',
          },
        }}
      >
        {renderContent}
      </Drawer>
    </Box>
  );
}
